import React, { useState } from 'react';
import {
  List,
  Show,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  DateField,
  TextField,
  BooleanField,
  NumberField,
  ShowButton,
  DeleteButton,
  CreateButton,
  TopToolbar,
  Datagrid,
  TabbedShowLayout,
  Tab,
  FunctionField,
  useDataProvider,
  useNotify,
  useRefresh,
  required,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PersonAdd, Upload, Delete as DeleteIcon } from '@mui/icons-material';

// User Summary Component
const UserSummary = ({ record }: { record: any }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        👤 User Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Address
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {record.address}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Vesting ID
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {record.vestingId}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Total Allocation
          </Typography>
          <Typography variant="body1">
            {record.totalAllocation ? (Number(record.totalAllocation) / 1e18).toFixed(2) : '0'} ATLA
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Claimed Amount
          </Typography>
          <Typography variant="body1">
            {record.claimedAmount ? (Number(record.claimedAmount) / 1e18).toFixed(2) : '0'} ATLA
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Eligible
          </Typography>
          <Chip 
            label={record.eligible ? 'Yes' : 'No'} 
            color={record.eligible ? 'success' : 'error'} 
            size="small" 
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Assigned to Vesting
          </Typography>
          <Chip 
            label={record.isAssignedToVesting ? 'Yes' : 'No'} 
            color={record.isAssignedToVesting ? 'success' : 'default'} 
            size="small" 
          />
        </Grid>
        {record.isAssignedToVesting && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              Assigned Vesting ID
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {record.assignedVestingId}
            </Typography>
          </Grid>
        )}
      </Grid>
    </CardContent>
  </Card>
);

// CSV Import Dialog Component
interface CSVUser {
  email: string;
  tokens: string;
  address: string;
  errors?: string[];
}

const CSVImportDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [csvData, setCsvData] = useState<CSVUser[]>([]);
  const [importing, setImporting] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Validate Ethereum address
  const isValidAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Parse CSV file
  const parseCSV = (text: string): CSVUser[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    // Skip header row
    const dataLines = lines.slice(1);
    const users: CSVUser[] = [];

    dataLines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Split by comma, handling quoted values
      const parts = trimmedLine.split(',').map(part => part.trim());
      
      if (parts.length < 3) {
        users.push({
          email: parts[0] || '',
          tokens: parts[1] || '',
          address: parts[2] || '',
          errors: ['Incomplete row data']
        });
        return;
      }

      const [email, tokens, address] = parts;
      const errors: string[] = [];

      // Validate email (optional, but check format)
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push('Invalid email format');
      }

      // Validate tokens (must be a number)
      const tokensNum = parseFloat(tokens);
      if (Number.isNaN(tokensNum) || tokensNum <= 0) {
        errors.push('Invalid tokens amount');
      }

      // Validate address
      if (!address || !isValidAddress(address)) {
        errors.push('Invalid Ethereum address');
      }

      users.push({
        email: email || '',
        tokens: tokens || '',
        address: address || '',
        errors: errors.length > 0 ? errors : undefined
      });
    });

    return users;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      setCsvData(parsed);
    };
    reader.onerror = () => {
      notify('Failed to read file', { type: 'error' });
    };
    reader.readAsText(file);
  };

  const handleRemoveRow = (index: number) => {
    setCsvData(prev => prev.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    const validUsers = csvData.filter(user => !user.errors || user.errors.length === 0);
    
    if (validUsers.length === 0) {
      notify('No valid users to import', { type: 'warning' });
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Import users one by one
      for (const user of validUsers) {
        try {
          const tokensNum = parseFloat(user.tokens);
          const totalAllocationInWei = (BigInt(Math.floor(tokensNum * 1e18))).toString();
          
          await dataProvider.create('users', {
            data: {
              address: user.address,
              totalAllocation: totalAllocationInWei,
              claimedAmount: '0',
              eligible: true,
            }
          });
          successCount++;
        } catch (error) {
          console.error('Failed to create user:', user.address, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        notify(`Successfully imported ${successCount} user(s)${errorCount > 0 ? `. ${errorCount} failed.` : ''}`, { 
          type: successCount === validUsers.length ? 'success' : 'warning' 
        });
        refresh();
        handleClose();
      } else {
        notify('Failed to import users', { type: 'error' });
      }
    } catch (_error) {
      notify('Import failed', { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setCsvData([]);
    setFileInputKey(prev => prev + 1);
    onClose();
  };

  const validUsers = csvData.filter(user => !user.errors || user.errors.length === 0);
  const invalidUsers = csvData.filter(user => user.errors && user.errors.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Users from CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, mt: 2 }}>
          <input
            key={fileInputKey}
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-file-input"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="csv-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload />}
              fullWidth
            >
              Upload CSV File
            </Button>
          </label>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            CSV format: Email,Tokens,Address (header row will be skipped)
          </Typography>
        </Box>

        {csvData.length > 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${validUsers.length} valid`} 
                color="success" 
                size="small" 
              />
              {invalidUsers.length > 0 && (
                <Chip 
                  label={`${invalidUsers.length} invalid`} 
                  color="error" 
                  size="small" 
                />
              )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Tokens (ATLA)</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.map((user, index) => {
                    const isValid = !user.errors || user.errors.length === 0;
                    return (
                      <TableRow 
                        key={index}
                        sx={{ 
                          backgroundColor: isValid ? 'transparent' : 'error.light',
                          opacity: isValid ? 1 : 0.7
                        }}
                      >
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.tokens}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {user.address}
                        </TableCell>
                        <TableCell>
                          {isValid ? (
                            <Chip label="Valid" color="success" size="small" />
                          ) : (
                            <Tooltip title={user.errors?.join(', ')}>
                              <Chip label="Invalid" color="error" size="small" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRow(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {invalidUsers.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {invalidUsers.length} user(s) have validation errors and will be skipped during import.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={importing || validUsers.length === 0}
          startIcon={importing ? <CircularProgress size={20} /> : <PersonAdd />}
        >
          {importing ? `Importing...` : `Import ${validUsers.length} User(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// User List Actions
const UserListActions = () => {
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  return (
    <TopToolbar>
      <Button
        variant="outlined"
        startIcon={<Upload />}
        onClick={() => setImportDialogOpen(true)}
        sx={{ mr: 1 }}
      >
        Import CSV
      </Button>
      <CreateButton />
      <CSVImportDialog 
        open={importDialogOpen} 
        onClose={() => setImportDialogOpen(false)} 
      />
    </TopToolbar>
  );
};

// User List
export const UserList = () => (
  <List actions={<UserListActions />}>
    <Datagrid>
      <TextField source="address" label="Address" />
      <TextField source="vestingId" label="Vesting ID" />
      <NumberField 
        source="totalAllocation" 
        label="Total Allocation (ATLA)" 
        transform={(value: string) => value ? Number(value) / 1e18 : 0}
        options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
      />
      <NumberField 
        source="claimedAmount" 
        label="Claimed (ATLA)" 
        transform={(value: string) => value ? Number(value) / 1e18 : 0}
        options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
      />
      <BooleanField source="eligible" label="Eligible" />
      <BooleanField source="isAssignedToVesting" label="Assigned" />
      <TextField source="assignedVestingId" label="Vesting Plan" />
      <DateField source="createdAt" label="Created" showTime />
      <ShowButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

// User Show
export const UserShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Summary">
        <FunctionField render={(record: any) => <UserSummary record={record} />} />
        <TextField source="address" label="Wallet Address" />
        <BooleanField source="eligible" label="Eligible" />
        <DateField source="createdAt" label="Created At" showTime />
        <DateField source="updatedAt" label="Updated At" showTime />
      </Tab>
      <Tab label="Assignment Details">
        <BooleanField source="isAssignedToVesting" label="Assigned to Vesting" />
        <TextField source="assignedVestingId" label="Assigned Vesting ID" />
        <DateField source="assignedAt" label="Assigned At" showTime />
      </Tab>
      <Tab label="Raw Data">
        <TextField source="totalAllocation" label="Total Allocation (wei)" />
        <TextField source="claimedAmount" label="Claimed Amount (wei)" />
        <FunctionField
          label="Metadata"
          render={(record: any) => (
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(record.metadata, null, 2)}
            </pre>
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

// User Edit
export const UserEdit = () => (
  <Edit>
    <SimpleForm>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>📝 Basic Information</Typography>
      <TextInput source="address" validate={[required()]} fullWidth disabled />
      <TextInput source="vestingId" validate={[required()]} fullWidth disabled />
      
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>💰 Allocation</Typography>
      <TextInput 
        source="totalAllocation" 
        label="Total Allocation (ATLA)" 
        validate={[required()]} 
        fullWidth 
        helperText="Amount in ATLA tokens"
      />
      <BooleanInput source="eligible" label="Eligible for claiming" />
    </SimpleForm>
  </Edit>
);

// User Create
export const UserCreate = () => (
  <Create transform={(data: any) => {
    // Convert ATLA to wei (1 ATLA = 10^18 wei)
    const totalAllocationInWei = data.totalAllocation ? 
      (BigInt(Math.floor(Number(data.totalAllocation) * 1e18))).toString() : 
      '0';
    
    return {
      ...data,
      totalAllocation: totalAllocationInWei,
      claimedAmount: '0', // Always 0 for new users
    };
  }}>
    <SimpleForm>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>📝 Basic Information</Typography>
      <TextInput source="address" validate={[required()]} fullWidth helperText="Ethereum wallet address" />
      <TextInput 
        source="totalAllocation" 
        label="Total Allocation (ATLA)" 
        validate={[required()]} 
        fullWidth 
        helperText="Amount in ATLA tokens (will be converted to wei automatically)"
      />
      <BooleanInput source="eligible" defaultValue={true} label="Eligible for claiming" />
    </SimpleForm>
  </Create>
);
